import { Store } from 'vuex';
import { cloneDeep } from 'lodash';
import fight from '@/store/modules/fight';
import bets from '@/store/modules/bets';
import { API_URLS } from '@/utils/constant';
import { getData, keysToCamel } from '@/utils/helpers';
import { IArena, IFight } from '@/utils/index.model';

/*
 * Per-pane cockfight state for the desktop client.
 *
 * `fight`, `bets` and `general/selectedArena` are single globals, and every
 * cockfight component (BetPanel, Charts, GameStatus, the players) is bound to
 * them by name. To let two desktop panes show two different cockfight arenas
 * without forking those components, each cockfight pane registers its own copy
 * of `fight` + `bets` under a pane namespace and hands its subtree a *scoped*
 * `$store`: a view of the root store in which `fight/*`, `bets/*` and
 * `general/selectedArena` resolve to the pane's copies, and everything else
 * (balance, config, jackpot, menus...) is the shared root. Mobile never goes
 * through here and keeps the plain root store.
 */

export type DesktopPaneId = 'a' | 'b';

const SCOPED_MODULES = ['fight', 'bets'];
const SELECTED_ARENA = 'general/selectedArena';

const paneRefCounts: { [namespace: string]: number } = {};

export const cockfightPaneNamespace = (paneId: DesktopPaneId): string => `cockfightPane_${paneId}`;

// Namespaces of the cockfight panes currently mounted, for the socket router.
export const getCockfightPaneNamespaces = (): string[] =>
  Object.keys(paneRefCounts).filter((namespace) => paneRefCounts[namespace] > 0);

const scopeType = (namespace: string, type: string): string => {
  if (type === SELECTED_ARENA) return `${namespace}/selectedArena`;
  const [module] = type.split('/');
  return SCOPED_MODULES.includes(module) ? `${namespace}/${type}` : type;
};

/*
 * Everything is read lazily off the root store: registering or unregistering a
 * module replaces `store.getters`, `store.state` and `_modulesNamespaceMap`
 * wholesale, so a proxy pinned to the old objects would go stale.
 */
const createScopedGetters = (store: Store<any>, namespace: string) =>
  new Proxy(
    {},
    {
      get: (_target, key) =>
        typeof key === 'string' ? store.getters[scopeType(namespace, key)] : undefined,
      has: (_target, key) => typeof key === 'string' && scopeType(namespace, key) in store.getters,
    }
  );

const createScopedState = (store: Store<any>, namespace: string) =>
  new Proxy(
    {},
    {
      get: (_target, key) => {
        if (typeof key !== 'string') return undefined;
        return SCOPED_MODULES.includes(key) ? store.state[namespace][key] : store.state[key];
      },
      has: (_target, key) => typeof key === 'string' && key in store.state,
    }
  );

/*
 * `mapActions('bets', [...])` and friends skip `$store.dispatch`: they look the
 * module up in `_modulesNamespaceMap` and use its own context. So the map has
 * to be scoped too, or namespaced helpers would reach the root copies.
 */
const createScopedNamespaceMap = (store: any, namespace: string) =>
  new Proxy(
    {},
    {
      get: (_target, key) => {
        if (typeof key !== 'string') return undefined;
        const moduleName = key.split('/')[0];
        const scopedKey = SCOPED_MODULES.includes(moduleName) ? `${namespace}/${key}` : key;
        return store._modulesNamespaceMap[scopedKey];
      },
    }
  );

const scopeCall = (namespace: string, type: any) =>
  typeof type === 'object' && type ? { ...type, type: scopeType(namespace, type.type) } : scopeType(namespace, type);

/*
 * A pane module's actions read `rootGetters['fight/fight']`,
 * `rootGetters['general/selectedArena']` and dispatch `bets/*` with
 * `{ root: true }` -- all of which must land on the pane's copies.
 */
const scopeActions = (store: Store<any>, namespace: string, actions: { [name: string]: any }) =>
  Object.keys(actions).reduce((scoped: { [name: string]: any }, name) => {
    const action = actions[name];
    scoped[name] = function scopedAction(this: any, context: any, payload: any) {
      const rootGetters = createScopedGetters(store, namespace);
      const dispatch = (type: any, data?: any, options?: any) =>
        options && options.root
          ? store.dispatch(scopeCall(namespace, type), data)
          : context.dispatch(type, data, options);
      const commit = (type: any, data?: any, options?: any) =>
        options && options.root
          ? store.commit(scopeCall(namespace, type), data, options)
          : context.commit(type, data, options);
      return action.call(this, { ...context, rootGetters, dispatch, commit }, payload);
    };
    return scoped;
  }, {});

/*
 * The root `getFightsForChart` asks over the main socket, whose FightResult
 * reply carries no match day, and always writes the root `fight` module. Two
 * panes asking at once could not tell their replies apart, so a pane loads its
 * road from REST instead -- the same endpoint Tài Xỉu's road chart uses.
 */
const paneChartActions = {
  async getFightsForChart(context: any, matchDayId: string): Promise<void> {
    if (!matchDayId) return;
    try {
      const response = await getData(API_URLS.FIGHT_RESULT, context.rootGetters['general/token'], {
        match_day_id: matchDayId,
      });
      const data = keysToCamel(response);
      const fights: IFight[] = (Array.isArray(data) ? data : data.results || data.fights || [])
        .slice()
        .sort((left: IFight, right: IFight) => Number(left.fightNumber) - Number(right.fightNumber));
      const count = (result: string) => fights.filter((item) => item.result === result).length;
      context.commit('INSERT_CHART_FIGHTS', fights);
      context.commit('INSERT_CHART_FIGHTS_TOTAL', {
        bankerCount: count('banker'),
        playerCount: count('player'),
        drawCount: count('draw'),
      });
    } catch (error) {
      console.error('[Pane chart]', error);
    }
  },
};

const createPaneModule = (store: Store<any>, namespace: string, arenaId: string) => ({
  namespaced: true,
  state: () => ({ arenaId }),
  mutations: {
    SET_ARENA(stateData: { arenaId: string }, id: string): void {
      stateData.arenaId = id;
    },
  },
  getters: {
    // Resolved from the shared arena list so live fight status reaches the pane.
    selectedArena: (stateData: { arenaId: string }, _getters: any, _rootState: any, rootGetters: any): IArena =>
      (rootGetters['general/arenas'] || []).find((arena: IArena) => arena.id === stateData.arenaId) ||
      ({} as IArena),
  },
  modules: {
    // Deep-copied state: the module templates share nested objects (e.g. the
    // bets `betHistory` array), which two live copies must not.
    fight: {
      ...fight,
      state: () => cloneDeep(fight.state()),
      actions: scopeActions(store, namespace, { ...fight.actions, ...paneChartActions }),
    },
    bets: {
      ...bets,
      state: () => cloneDeep(bets.state()),
      actions: scopeActions(store, namespace, bets.actions),
    },
  },
});

/*
 * Registers the pane's state (once per pane, reference counted so a remount
 * that overlaps its predecessor's teardown cannot drop it) and returns the
 * scoped store view for the pane's component tree.
 */
export const acquireCockfightPaneStore = (store: Store<any>, paneId: DesktopPaneId, arenaId: string): Store<any> => {
  const namespace = cockfightPaneNamespace(paneId);
  if (!paneRefCounts[namespace]) {
    store.registerModule(namespace, createPaneModule(store, namespace, arenaId));
  }
  paneRefCounts[namespace] = (paneRefCounts[namespace] || 0) + 1;

  const getters = createScopedGetters(store, namespace);
  const state = createScopedState(store, namespace);
  const namespaceMap = createScopedNamespaceMap(store, namespace);

  // Inherit from the root so watch/subscribe/etc. keep working, and they run
  // against the scoped state and getters because `this` is the view.
  const scoped = Object.create(store);
  Object.defineProperties(scoped, {
    getters: { get: () => getters },
    state: { get: () => state },
    _modulesNamespaceMap: { get: () => namespaceMap },
  });
  scoped.dispatch = (type: any, payload?: any) => store.dispatch(scopeCall(namespace, type), payload);
  scoped.commit = (type: any, payload?: any, options?: any) =>
    store.commit(scopeCall(namespace, type), payload, options);
  return scoped as Store<any>;
};

export const releaseCockfightPaneStore = (store: Store<any>, paneId: DesktopPaneId): void => {
  const namespace = cockfightPaneNamespace(paneId);
  if (!paneRefCounts[namespace]) return;
  paneRefCounts[namespace] -= 1;
  if (paneRefCounts[namespace] > 0) return;
  delete paneRefCounts[namespace];
  store.unregisterModule(namespace);
};
